<template>
  <div class="portfolio-view">
    <header class="pv-header">
      <div>
        <h1>Portfolio</h1>
        <p class="pv-sub">Shoots, images and home-feed curation</p>
      </div>
      <div class="pv-tabs">
        <button :class="['tab', { active: tab === 'shoots' }]" @click="tab = 'shoots'">Shoots</button>
        <button :class="['tab', { active: tab === 'home' }]" @click="openHomeFeed">Feed</button>
      </div>
    </header>

    <!-- ─────────── SHOOTS TAB ─────────── -->
    <div v-if="tab === 'shoots'" class="pv-body">
      <!-- Left: shoots list -->
      <aside class="pv-list">
        <div class="pv-list-head">
          <input v-model="shootQuery" @input="loadShoots" placeholder="Search shoots…" class="pv-search" />
          <button class="btn-accent sm" @click="startNewShoot">+ New</button>
        </div>
        <div v-if="loadingShoots" class="pv-muted">Loading…</div>
        <ul v-else class="shoot-list">
          <li
            v-for="s in shoots" :key="s.id"
            :class="{ active: selectedShoot?.id === s.id }"
            @click="selectShoot(s.id)">
            <span class="shoot-name">{{ s.name }}</span>
            <span class="shoot-meta">
              <span v-if="s.category" class="chip">{{ s.category }}</span>
              <span class="count">{{ s.image_count }} img</span>
            </span>
          </li>
          <li v-if="!shoots.length" class="pv-muted">No shoots yet.</li>
        </ul>
      </aside>

      <!-- Right: shoot detail / editor -->
      <section class="pv-detail">
        <!-- New / edit shoot form -->
        <div v-if="editingShoot" class="card">
          <h2>{{ shootForm.id ? 'Edit shoot' : 'New shoot' }}</h2>
          <div class="form-grid">
            <label>Name<input v-model="shootForm.name" placeholder="e.g. Eilish — Swimsuit" /></label>
            <label class="span2">Categories <span class="pv-muted">(a shoot can be in several — first is primary)</span>
              <div class="cat-multi">
                <CategoryPicker v-model="shootCats" :options="categoryOptions" />
                <button type="button" class="btn-ghost sm" @click="startNewCategory" title="Create a new category">+ New category</button>
              </div>
            </label>
            <label>Internal slug (never shown publicly)
              <input v-model="shootForm.slug" placeholder="2022/405-eilish-swimsuit" />
            </label>
            <label>Shoot date<input type="date" v-model="shootForm.shootDate" /></label>
            <label class="span2">Location
              <div class="loc-picker">
                <div v-if="shootForm.locationId" class="loc-picked">
                  <span class="loc-picked-label">📍 {{ shootForm.locationVenue || shootForm.locationCity || 'Saved location' }}
                    <span class="pv-muted" v-if="shootForm.locationCity || shootForm.locationState">· {{ [shootForm.locationCity, shootForm.locationState].filter(Boolean).join(', ') }}</span>
                  </span>
                  <button type="button" class="btn-ghost sm" @click="clearShootLocation" title="Clear and type a one-off location">Change</button>
                </div>
                <template v-else>
                  <input
                    v-model="shootLocQuery"
                    placeholder="Type to search saved locations, or fill the fields below for a one-off…"
                    @input="onShootLocationSearch"
                    @focus="shootLocPickerOpen = true; onShootLocationSearch()"
                    @blur="shootLocBlur"
                  />
                  <ul v-if="shootLocPickerOpen && shootLocResults.length" class="contrib-suggest">
                    <li v-for="l in shootLocResults" :key="l.id" @mousedown.prevent="pickShootLocation(l)">
                      <strong>{{ locLabel(l) }}</strong>
                      <span class="contrib-meta" v-if="l.location_address">{{ l.location_address }}</span>
                    </li>
                  </ul>
                </template>
              </div>
            </label>
            <label>Venue / place<input v-model="shootForm.locationVenue" placeholder="e.g. Hotel Icon" /></label>
            <label class="span2">Address<input v-model="shootForm.locationAddress" placeholder="e.g. 220 Main St" /></label>
            <label>City<input v-model="shootForm.locationCity" placeholder="e.g. Houston" /></label>
            <label>State<input v-model="shootForm.locationState" placeholder="e.g. TX" /></label>
            <label class="span2">Notes<textarea v-model="shootForm.notes" rows="2" /></label>
          </div>

          <!-- New-category inline form -->
          <div v-if="newCategory.active" class="new-category-row">
            <input v-model="newCategory.label" placeholder="Category name (e.g. Boudoir)" @keyup.enter="saveNewCategory" />
            <button class="btn-accent sm" @click="saveNewCategory">Create</button>
            <button class="btn-ghost sm" @click="newCategory.active = false">Cancel</button>
            <span v-if="newCategory.status" class="pv-muted">{{ newCategory.status }}</span>
          </div>

          <!-- Shoot credits: contributors (models/crew) + the role each filled on THIS shoot.
               The same contributor can appear in multiple roles (model + hair). -->
          <div class="credits-editor">
            <div class="credit-group-head">
              <h4>Credits</h4>
              <button class="btn-ghost sm" @click="addCredit()">+ Add credit</button>
            </div>
            <p class="pv-muted credit-hint">Search someone you've worked with (so you don't duplicate them), then set what they did on this shoot.</p>
            <div v-for="(cr, i) in shootCredits" :key="'cr-' + i" class="credit-row">
              <div class="credit-person">
                <input
                  v-model="cr.name"
                  :placeholder="'Name — type to search'"
                  @input="onContributorSearch(cr, i)"
                  @focus="contribActiveRow = i; onContributorSearch(cr, i)"
                />
                <ul v-if="contribActiveRow === i && contribResults.length" class="contrib-suggest">
                  <li v-for="r in contribResults" :key="r.id" @mousedown.prevent="pickContributor(cr, r)">
                    <strong>{{ r.name }}</strong>
                    <span class="contrib-meta">
                      <template v-if="r.business_name">{{ r.business_name }} · </template>
                      {{ r.shoot_count }} shoot{{ r.shoot_count === 1 ? '' : 's' }}<template v-if="r.last_worked"> · last {{ fmtYear(r.last_worked) }}</template>
                      <template v-if="r.roles?.length"> · {{ r.roles.join(', ') }}</template>
                    </span>
                  </li>
                </ul>
                <span v-if="cr.contributorId" class="contrib-linked" title="Linked to an existing contributor">✓ known</span>
              </div>
              <select v-model="cr.role" class="credit-role">
                <option v-for="r in CREDIT_ROLE_OPTIONS" :key="r.value" :value="r.value">{{ r.label }}</option>
              </select>
              <button class="btn-ghost sm" @click="editContributor(cr)" :disabled="!cr.contributorId && !cr.name.trim()" title="Edit this person's socials/details">socials</button>
              <button class="link-danger" @click="removeCredit(i)" title="Remove">✕</button>
            </div>
            <p v-if="!shootCredits.length" class="pv-muted credit-empty">No credits yet.</p>
          </div>

          <!-- Contributor editor (socials) drawer -->
          <div v-if="contribEdit.active" class="contrib-editor">
            <h4>{{ contribEdit.id ? 'Edit' : 'New' }} contributor</h4>
            <div class="form-grid">
              <label>Name<input v-model="contribEdit.name" placeholder="Full name" /></label>
              <label>Business (optional)<input v-model="contribEdit.businessName" placeholder="Studio / brand" /></label>
              <label class="span2">Notes<textarea v-model="contribEdit.notes" rows="2" /></label>
            </div>
            <SocialLinksEditor v-model="contribEdit.socials" label="Social media" />
            <div class="row-actions">
              <button class="btn-accent sm" @click="saveContributor">Save contributor</button>
              <button class="btn-ghost sm" @click="contribEdit.active = false">Done</button>
              <span v-if="contribEdit.status" class="pv-muted">{{ contribEdit.status }}</span>
            </div>
          </div>

          <div class="row-actions">
            <button class="btn-accent" @click="saveShoot">Save</button>
            <button class="btn-ghost" @click="cancelShootEdit">Cancel</button>
          </div>
        </div>

        <!-- Selected shoot images -->
        <div v-else-if="selectedShoot" class="card">
          <div class="detail-head">
            <div>
              <h2>{{ selectedShoot.name }}</h2>
              <p class="pv-muted">
                <span v-if="selectedShoot.category" class="chip">{{ selectedShoot.category }}</span>
                {{ selectedShoot.images?.length || 0 }} images
                <span v-if="selectedShoot.slug" class="slug">· {{ selectedShoot.slug }}</span>
              </p>
              <p v-if="shootLocation(selectedShoot)" class="pv-muted">📍
                <a href="#" class="link-chip" @click.prevent="openLocationModal(selectedShoot)">{{ shootLocation(selectedShoot) }}</a>
              </p>
              <p v-if="selectedShoot.credits?.length" class="pv-muted shoot-credits-summary">
                <span v-for="(c, ci) in selectedShoot.credits" :key="ci" class="credit-chip">
                  {{ roleLabel(c.role) }}: <a href="#" class="link-chip" @click.prevent="openContributorModal(c)">{{ c.name }}</a>
                </span>
              </p>
            </div>
            <div class="row-actions">
              <button class="btn-ghost sm" @click="editShoot(selectedShoot)">Edit shoot</button>
              <button class="btn-ghost sm" @click="openApplyAttrs" :disabled="!selectedShoot.images?.length" title="Set common properties on every photo in this shoot">Apply attributes</button>
              <label class="btn-accent sm file-btn">
                + Add images
                <input type="file" accept="image/*" multiple @change="onUploadFiles" hidden />
              </label>
              <label class="btn-ghost sm file-btn">
                + Add folder
                <input type="file" accept="image/*" multiple webkitdirectory directory @change="onUploadFiles" hidden />
              </label>
              <label class="btn-ghost sm file-btn" title="Pick the folder of RAW originals. Your browser may ask to &quot;upload all files&quot; just to read the folder — nothing is sent yet. You then review the name matches and only the matched RAWs are uploaded.">
                Find RAWs
                <input type="file" multiple webkitdirectory directory @change="onPickRawFolder" hidden />
              </label>
            </div>
          </div>

          <!-- Apply-attributes-to-whole-shoot panel -->
          <div class="apply-attrs" v-if="applyAttrs.open">
            <div class="apply-head">
              <strong>Apply attributes to all {{ selectedShoot.images?.length || 0 }} photos</strong>
              <button class="btn-ghost sm" @click="applyAttrs.open = false" :disabled="applyAttrs.busy" title="Close">✕</button>
            </div>
            <p class="pv-muted">Tick what to set; untouched fields are left as-is on each photo.</p>
            <div class="apply-rows">
              <div class="apply-row">
                <input type="checkbox" v-model="applyAttrs.setCategory" :disabled="applyAttrs.busy" />
                <span class="apply-label">Category</span>
                <div class="apply-field" :class="{ 'is-disabled': !applyAttrs.setCategory || applyAttrs.busy }">
                  <CategoryPicker v-model="applyAttrs.categories" :options="categoryOptions" />
                </div>
              </div>
              <label class="apply-row">
                <input type="checkbox" v-model="applyAttrs.setLocation" :disabled="applyAttrs.busy" />
                <span class="apply-label">Location</span>
                <div class="apply-field" :class="{ 'is-disabled': !applyAttrs.setLocation || applyAttrs.busy }">
                  <LocationPicker v-model="applyAttrs.locationText" :locations="savedLocations"
                                  :placeholder="shootLocation(selectedShoot) || 'Type to search saved locations…'" />
                </div>
                <button type="button" class="btn-ghost sm" v-if="applyAttrs.setLocation && shootLocation(selectedShoot)"
                        @click="applyAttrs.locationText = shootLocation(selectedShoot)" title="Use the shoot's location">Use shoot</button>
              </label>
              <label class="apply-row">
                <input type="checkbox" v-model="applyAttrs.setPeople" :disabled="applyAttrs.busy" />
                <span class="apply-label">People</span>
                <div class="apply-field" :class="{ 'is-disabled': !applyAttrs.setPeople || applyAttrs.busy }">
                  <PeoplePicker v-model="applyPeople" />
                </div>
              </label>
              <label class="apply-row">
                <input type="checkbox" v-model="applyAttrs.setTakenDate" :disabled="applyAttrs.busy" />
                <span class="apply-label">Taken date</span>
                <input type="date" v-model="applyAttrs.takenDate" :disabled="!applyAttrs.setTakenDate || applyAttrs.busy" />
                <button type="button" class="btn-ghost sm" v-if="applyAttrs.setTakenDate && selectedShoot.shoot_date"
                        @click="applyAttrs.takenDate = selectedShoot.shoot_date.slice(0,10)" title="Use the shoot date">Use shoot date</button>
              </label>
            </div>
            <div class="apply-actions">
              <button class="btn-accent sm" @click="runApplyAttrs" :disabled="applyAttrs.busy || !applyAttrsAnySelected()">
                {{ applyAttrs.busy ? `Applying ${applyAttrs.done}/${selectedShoot.images?.length || 0}…` : 'Apply to all photos' }}
              </button>
              <button class="btn-ghost sm" @click="applyAttrs.open = false" :disabled="applyAttrs.busy">Cancel</button>
              <span v-if="applyAttrs.status" class="pv-muted">{{ applyAttrs.status }}</span>
            </div>
          </div>

          <!-- Drag & drop bulk upload zone -->
          <div class="dropzone" :class="{ dragging: isDragging }"
               @dragenter.prevent="isDragging = true"
               @dragover.prevent="isDragging = true"
               @dragleave.prevent="isDragging = false"
               @drop.prevent="onDropFiles">
            <p v-if="!uploadQueue.length">Drag & drop images or a folder here, or use the buttons above.</p>
            <div v-else class="upload-progress">
              <p class="pv-muted">{{ uploadDone }} / {{ uploadQueue.length }} uploaded{{ uploadFailed ? ` · ${uploadFailed} failed` : '' }}</p>
              <div class="bar"><div class="bar-fill" :style="{ width: uploadPct + '%' }"></div></div>
            </div>
          </div>

          <p v-if="uploadStatus" class="pv-muted">{{ uploadStatus }}</p>

          <!-- Duplicate-upload prompt: a byte-identical image already exists in this shoot -->
          <ModalShell
            :open="dupePrompt.open"
            title="⚠ This image already exists"
            max-width="460px"
            @update:open="(v) => { if (!v) resolveDupe('skip') }"
          >
            <p class="dupe-text">
              <strong>{{ dupePrompt.file?.name }}</strong> is byte-for-byte identical to an image already in this shoot<template v-if="dupePrompt.existing?.displayName"> (“{{ dupePrompt.existing?.displayName }}”)</template>.
            </p>
            <p class="pv-muted dupe-sub">What would you like to do?</p>
            <div class="dupe-actions">
              <button class="btn-accent sm" @click="resolveDupe('overwrite', dupeAllChecked)">Overwrite existing</button>
              <button class="btn-ghost sm" @click="resolveDupe('add', dupeAllChecked)">Add anyway (keep both)</button>
              <button class="btn-ghost sm" @click="resolveDupe('skip', dupeAllChecked)">Skip this one</button>
            </div>
            <label class="dupe-all" v-if="uploadQueue.length > 1">
              <input type="checkbox" v-model="dupeAllChecked" />
              Apply my choice to all remaining duplicates in this batch
            </label>
          </ModalShell>

          <!-- RAW folder matcher: review panel -->
          <div class="raw-match" v-if="rawMatch.open">
            <div class="raw-match-head">
              <strong>Match RAWs</strong>
              <span class="pv-muted" v-if="rawMatch.folderName">from “{{ rawMatch.folderName }}”</span>
              <button class="btn-ghost sm raw-match-close" @click="closeRawMatch" :disabled="rawMatch.busy" title="Cancel">✕</button>
            </div>
            <p class="pv-muted raw-match-summary">
              {{ rawMatch.matched.length }} of {{ selectedShoot.images?.length || 0 }} photos matched to a RAW file
              <template v-if="rawMatch.unmatchedImages.length">· {{ rawMatch.unmatchedImages.length }} photo{{ rawMatch.unmatchedImages.length === 1 ? '' : 's' }} with no RAW</template>
              <template v-if="rawMatch.rawFileCount">· {{ rawMatch.rawFileCount }} RAW file{{ rawMatch.rawFileCount === 1 ? '' : 's' }} in folder</template>
            </p>
            <p class="pv-muted raw-match-note" v-if="rawMatch.rawFileCount > rawMatch.matched.length">
              Only the {{ rawMatch.matched.length }} matched RAW{{ rawMatch.matched.length === 1 ? '' : 's' }} below will upload — the other files in the folder are ignored. (Your browser scans the whole folder to find matches, which is why it asked about “all files.”)
            </p>
            <ul class="raw-match-list" v-if="rawMatch.matched.length">
              <li v-for="m in rawMatch.matched" :key="m.imageId" class="raw-match-row ok">
                <span class="rm-name">{{ m.displayName }}</span>
                <span class="rm-arrow">→</span>
                <span class="rm-raw">{{ m.rawName }}</span>
                <span v-if="m.alreadyHasRaw" class="rm-note pv-muted" title="This photo already has a RAW; matching will replace it">replaces existing</span>
              </li>
            </ul>
            <details v-if="rawMatch.unmatchedImages.length" class="raw-match-unmatched">
              <summary class="pv-muted">{{ rawMatch.unmatchedImages.length }} photo{{ rawMatch.unmatchedImages.length === 1 ? '' : 's' }} with no matching RAW</summary>
              <ul class="raw-match-list">
                <li v-for="n in rawMatch.unmatchedImages" :key="n" class="raw-match-row miss"><span class="rm-name">{{ n }}</span></li>
              </ul>
            </details>
            <div class="raw-match-actions">
              <button class="btn-accent sm" @click="commitRawMatch" :disabled="!rawMatch.matched.length || rawMatch.busy">
                {{ rawMatch.busy ? `Uploading ${rawMatch.done}/${rawMatch.matched.length}…` : `Upload & attach ${rawMatch.matched.length} RAW${rawMatch.matched.length === 1 ? '' : 's'}` }}
              </button>
              <button class="btn-ghost sm" @click="closeRawMatch" :disabled="rawMatch.busy">Cancel</button>
              <span v-if="rawMatch.status" class="pv-muted">{{ rawMatch.status }}</span>
            </div>
          </div>

          <!-- Multi-select bulk toolbar -->
          <div class="bulk-bar" v-if="selectedShoot.images?.length">
            <div class="bulk-left">
              <button class="btn-ghost sm" @click="selectAll">Select all</button>
              <button class="btn-ghost sm" @click="clearSelection" :disabled="!selectedCount()">Clear</button>
              <span class="pv-muted" v-if="selectedCount()">{{ selectedCount() }} selected</span>
            </div>
            <div class="bulk-actions" v-if="selectedCount()">
              <select v-model="bulkCategory" class="bulk-input" :disabled="bulkBusy">
                <option value="">Set category…</option>
                <option v-for="c in categories" :key="c.slug" :value="c.slug">{{ c.label }}</option>
              </select>
              <button class="btn-ghost sm" @click="bulkSetCategory" :disabled="!bulkCategory || bulkBusy">Apply</button>
              <input type="date" v-model="bulkTakenDate" class="bulk-input" :disabled="bulkBusy" title="Set taken date" />
              <button class="btn-ghost sm" @click="bulkSetTakenDate" :disabled="!bulkTakenDate || bulkBusy">Apply</button>
              <button class="btn-ghost sm" @click="bulkGenerate" :disabled="bulkBusy" title="Generate variants for selected">Generate variants</button>
              <template v-if="bulkDeleteConfirm">
                <span class="delete-confirm-label">Delete {{ selectedCount() }}?</span>
                <button class="btn-danger sm" @click="bulkDelete" :disabled="bulkBusy">Yes, delete</button>
                <button class="btn-ghost sm" @click="bulkDeleteConfirm = false">Cancel</button>
              </template>
              <button v-else class="btn-danger sm" @click="bulkDeleteConfirm = true" :disabled="bulkBusy">Delete</button>
            </div>
            <span v-if="bulkStatus" class="pv-muted bulk-status">{{ bulkStatus }}</span>
          </div>

          <div class="img-grid">
            <figure v-for="(img, idx) in selectedShoot.images" :key="img.id"
                    :class="{ sel: detailImage?.id === img.id, 'multi-sel': isSelected(img.id), 'drag-over': dragOverIdx === idx, dragging: dragFromIdx === idx }"
                    draggable="true"
                    @dragstart="onDragStart(idx, $event)"
                    @dragover.prevent="onDragOver(idx)"
                    @dragleave="onDragLeave(idx)"
                    @drop.prevent="onDrop(idx)"
                    @dragend="onDragEnd"
                    @click="openImage(img)">
              <label class="sel-check" :class="{ on: isSelected(img.id) }" @click.stop title="Select">
                <input type="checkbox" :checked="isSelected(img.id)" @change="toggleSelect(img.id)" />
              </label>
              <div class="thumb" :class="img.orientation || ''">
                <img v-if="thumbUrl(img)" :src="thumbUrl(img)" :alt="img.alt_text || img.display_name || ''" loading="lazy" />
                <span v-else class="no-thumb">
                  <span class="spinner"></span>
                  generating…
                </span>
                <span class="var-badge" :class="variantStatus(img)"
                      :title="variantStatus(img) === 'complete' ? 'All variants generated'
                            : variantStatus(img) === 'generating' ? 'Variants generating…'
                            : 'Waiting to generate'">
                  <template v-if="variantStatus(img) === 'complete'">✓</template>
                  <template v-else-if="variantStatus(img) === 'generating'">{{ presentRequiredCount(img) }}/{{ expectedVariantCount(img) }}</template>
                  <template v-else>⏳</template>
                </span>
              </div>
              <figcaption>{{ img.display_name || img.title || ('img ' + img.id) }}</figcaption>
            </figure>
            <p v-if="!selectedShoot.images?.length" class="pv-muted">No images yet — add one.</p>
          </div>
        </div>

        <div v-else class="card empty">
          <p class="pv-muted">Select a shoot, or create one.</p>
        </div>

        <!-- Image detail drawer -->
        <div v-if="detailImage" class="card image-detail">
          <div class="detail-head">
            <h3>{{ detailImage.display_name || detailImage.id }}</h3>
            <span v-if="saveMetaStatus" class="save-status pv-muted">{{ saveMetaStatus }}</span>
            <button class="btn-ghost sm detail-close" @click="detailImage = null" title="Close">✕</button>
          </div>

          <div class="image-detail-body">
            <div class="image-detail-preview" :class="{ clickable: !!lightboxUrl(detailImage) }"
                 @click="openLightbox(detailImage)"
                 :title="lightboxUrl(detailImage) ? 'Click to view full screen' : ''">
              <img v-if="thumbUrl(detailImage)" :src="thumbUrl(detailImage)"
                   :alt="detailImage.alt_text || detailImage.display_name || ''" loading="lazy" />
              <span v-else class="no-thumb">no variant</span>
            </div>

            <div class="image-detail-right">
              <div class="image-detail-main">
                <div class="form-grid">
                  <label>Display name<input v-model="imgForm.displayName" placeholder="Katie and George Wedding" /></label>
                  <label>Title<input v-model="imgForm.title" /></label>
                  <label class="span2">Categories
                    <CategoryPicker v-model="imgCats" :options="categoryOptions" />
                  </label>
                  <label>Taken date<input type="date" v-model="imgForm.takenDate" /></label>
                  <label class="span2">Alt text<input v-model="imgForm.altText" /></label>
                  <label class="span2">Location
                    <LocationPicker v-model="imgForm.locationText" :locations="savedLocations" />
                  </label>
                  <label class="span2">People
                    <PeoplePicker v-model="imgPeople" />
                  </label>
                  <label class="span2">Capture group
                    <input v-model="imgForm.captureGroup" placeholder="Auto from filename (e.g. Katie-George-42)" />
                  </label>
                </div>

                <div class="row-actions detail-controls">
                  <button class="btn-accent sm" @click="saveImageMeta">Save metadata</button>
                  <button v-if="detailImage.raw_s3_key" class="btn-ghost sm" @click="downloadRaw">Download RAW</button>
                  <template v-if="deleteConfirm">
                    <span class="delete-confirm-label">Delete this image?</span>
                    <button class="btn-danger sm" @click="deleteImage">Yes, delete</button>
                    <button class="btn-ghost sm" @click="deleteConfirm = false">Cancel</button>
                  </template>
                  <button v-else class="btn-danger sm" @click="deleteConfirm = true">Delete image</button>
                </div>
              </div>

              <div class="variants">
                <div class="variants-head">
                  <h4>Variants</h4>
                  <div class="variants-actions">
                    <button class="btn-ghost sm" @click="generateVariants" title="Run the resize assist (sizes + AVIF/WebP/JPG; full size stays JPG)">
                      Generate variants
                    </button>
                    <button class="btn-ghost sm" @click="generateFullFormats" title="Also generate full-resolution WebP + AVIF (slower, larger files)">
                      Full-size WebP/AVIF
                    </button>
                    <label class="btn-ghost sm file-btn">
                      Upload RAW
                      <input type="file" accept=".nef,.tif,.tiff,.dng,image/tiff" @change="onUploadRaw" hidden />
                    </label>
                  </div>
                </div>
                <p v-if="genStatus" class="pv-muted">{{ genStatus }}</p>
                <table v-if="detailImage.variants?.length">
                  <thead><tr><th>Label</th><th>Size</th><th>Format</th><th>Bytes</th><th></th></tr></thead>
                  <tbody>
                    <tr v-for="v in detailImage.variants" :key="v.id">
                      <td>
                        <a v-if="variantUrl(v)" :href="variantUrl(v)" target="_blank" rel="noopener" class="variant-link">{{ v.label }}</a>
                        <span v-else>{{ v.label }}</span>
                      </td>
                      <td>{{ v.width }}×{{ v.height }}</td>
                      <td>{{ v.format }}</td>
                      <td>{{ v.bytes ? (v.bytes / 1024).toFixed(0) + ' KB' : '—' }}</td>
                      <td><button class="link-danger" @click="deleteVariant(v.id)">delete</button></td>
                    </tr>
                  </tbody>
                </table>
                <p v-else class="pv-muted">No variants yet. Upload sizes manually or use “Generate variants”.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- ─────────── FEED / PLACEMENTS TAB ─────────── -->
    <div v-else class="pv-body home-feed">
      <section class="card">
        <div class="feed-head">
          <h2>Curated feed</h2>
          <label class="surface-pick">Surface
            <select v-model="curSurface" @change="loadSurface">
              <option value="home">Home page</option>
              <option v-for="c in categories" :key="c.slug" :value="'category:' + c.slug">Category — {{ c.label }}</option>
            </select>
          </label>
          <div class="feed-head-actions">
            <button class="btn-accent sm" @click="saveHomeFeed">Save order</button>
            <span v-if="feedStatus" class="pv-muted">{{ feedStatus }}</span>
          </div>
        </div>
        <p class="pv-muted">Drag to reorder. These images, in this order, appear on the selected surface.</p>
        <ul class="feed-list">
          <li v-for="(img, i) in homeFeed" :key="img.id" draggable="true"
              :class="{ dragging: dragFrom === i, 'drag-over': dragOver === i && dragFrom !== i }"
              @dragstart="dragFrom = i" @dragover.prevent="dragOver = i" @dragleave="dragOver === i && (dragOver = null)" @drop="dropAt(i)" @dragend="dragFrom = null; dragOver = null">
            <span class="grip">⋮⋮</span>
            <img v-if="feedThumb(img)" :src="feedThumb(img)" :alt="img.alt_text || ''" />
            <span class="feed-name">{{ img.display_name || img.title || ('img ' + img.id) }}</span>
            <button class="link-danger" @click="removeFromFeed(i)">remove</button>
          </li>
          <li v-if="!homeFeed.length" class="pv-muted feed-empty">
            No images curated for this surface yet.
            <template v-if="curSurface.startsWith('category:')">
              The public <strong>{{ curSurface.slice('category:'.length) }}</strong> page is currently auto-filling from all images in that category. Add images here to curate an explicit order.
            </template>
          </li>
        </ul>
        <div class="row-actions">
          <button class="btn-accent" @click="saveHomeFeed">Save order</button>
          <span v-if="feedStatus" class="pv-muted">{{ feedStatus }}</span>
        </div>
      </section>

      <aside class="card">
        <h3>Add from shoots</h3>
        <input v-model="pickerQuery" @input="loadShoots" placeholder="Search shoots…" class="pv-search" />
        <div v-for="s in shoots" :key="s.id" class="picker-shoot" :class="{ open: pickerOpen === s.id }">
          <button class="picker-head" :class="{ open: pickerOpen === s.id }" @click="togglePicker(s.id)">
            <span class="picker-chevron">{{ pickerOpen === s.id ? '▾' : '▸' }}</span>
            <span class="picker-title">{{ s.name }} ({{ s.image_count }})</span>
          </button>
          <div v-if="pickerOpen === s.id" class="picker-grid">
            <figure v-for="img in (pickerImages[s.id] || [])" :key="img.id" @click="addToFeed(img)">
              <img v-if="thumbUrl(img)" :src="thumbUrl(img)" :alt="''" loading="lazy" />
              <span v-else class="no-thumb">+</span>
            </figure>
          </div>
        </div>
      </aside>
    </div>

    <!-- ─────────── FULL-SCREEN IMAGE LIGHTBOX ─────────── -->
    <div v-if="lightboxSrc" class="lightbox" @click="lightboxSrc = null">
      <button class="lightbox-close" @click.stop="lightboxSrc = null" title="Close (Esc)">✕</button>
      <img :src="lightboxSrc" class="lightbox-img" @click.stop alt="Full-size preview" />
    </div>

    <!-- ─────────── LOCATION INFO MODAL (read-only) ─────────── -->
    <ModalShell v-model:open="infoLoc.open" title="📍 Location" :loading="infoLoc.loading">
      <p v-if="infoLoc.data.venue" class="info-line"><strong>{{ infoLoc.data.venue }}</strong>
        <span v-if="infoLoc.data.is_closed" class="closed-badge">Closed</span>
      </p>
      <p v-if="infoLoc.data.address" class="pv-muted">{{ infoLoc.data.address }}</p>
      <p v-if="infoLoc.data.city || infoLoc.data.state" class="pv-muted">{{ [infoLoc.data.city, infoLoc.data.state].filter(Boolean).join(', ') }}</p>
      <p v-if="infoLoc.data.url"><a :href="infoLoc.data.url" target="_blank" rel="noopener" class="link-chip">{{ infoLoc.data.url }}</a></p>
      <p v-if="infoLoc.data.email"><a :href="'mailto:' + infoLoc.data.email" class="link-chip">{{ infoLoc.data.email }}</a></p>
      <p v-if="infoLoc.data.socials?.length" class="info-socials">
        <SocialLinks :socials="infoLoc.data.socials" />
      </p>
      <p v-if="infoLoc.data.notes" class="info-notes">{{ infoLoc.data.notes }}</p>
      <p v-if="!infoLoc.found" class="pv-muted">This is a one-off location (not saved to the Library), so only the shoot's own fields are shown.</p>
    </ModalShell>

    <!-- ─────────── CONTRIBUTOR INFO MODAL (read-only) ─────────── -->
    <ModalShell v-model:open="infoContrib.open" :title="infoContrib.data.name || 'Person'" :loading="infoContrib.loading">
      <img v-if="infoContrib.data.photo_s3_key" :src="photoUrl(infoContrib.data.photo_s3_key)" class="info-avatar" alt="" />
      <p v-if="infoContrib.data.business_name" class="pv-muted">{{ infoContrib.data.business_name }}</p>
      <p v-if="infoContrib.roleHere" class="pv-muted">Role on this shoot: <strong>{{ infoContrib.roleHere }}</strong></p>
      <p v-if="infoContrib.data.primary_roles?.length" class="pv-muted">Does: {{ infoContrib.data.primary_roles.join(', ') }}</p>
      <p v-if="infoContrib.data.city || infoContrib.data.state" class="pv-muted">{{ [infoContrib.data.city, infoContrib.data.state].filter(Boolean).join(', ') }}</p>
      <p v-if="infoContrib.data.email"><a :href="'mailto:' + infoContrib.data.email" class="link-chip">{{ infoContrib.data.email }}</a></p>
      <p v-if="infoContrib.data.phone" class="pv-muted">{{ infoContrib.data.phone }}</p>
      <p v-if="infoContrib.data.socials?.length" class="info-socials">
        <SocialLinks :socials="infoContrib.data.socials" />
      </p>
      <p v-if="infoContrib.data.notes" class="info-notes">{{ infoContrib.data.notes }}</p>
      <p v-if="!infoContrib.found" class="pv-muted">This person isn't in the Library yet — only the shoot credit is shown.</p>
    </ModalShell>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAdminApi, photoUrl } from '@/composables/useAdminApi'
import SocialLinksEditor from '@/components/SocialLinksEditor.vue'
import SocialLinks from '@/components/SocialLinks.vue'
import ModalShell from '@/components/ModalShell.vue'
import CategoryPicker from '@/components/CategoryPicker.vue'
import LocationPicker from '@/components/LocationPicker.vue'
import PeoplePicker from '@/components/PeoplePicker.vue'
import { expectedVariantKeys, normalizeFormat } from '@shared/imageSizes'

const { apiFetch } = useAdminApi()

// Categories are DB-backed (admin-creatable). Loaded on mount; falls back to the
// legacy fixed set if the endpoint isn't available yet (pre-migration).
const FALLBACK_CATEGORIES = ['bridal','fashion','headshots','seniors','creative','modeling','weddings','graduation','maternity']
const categories = ref<Array<{ slug: string; label: string }>>(
  FALLBACK_CATEGORIES.map(s => ({ slug: s, label: s }))
)
async function loadCategories() {
  try {
    const res = await apiFetch('/admin/portfolio/categories')
    const list = (await res.json()).categories
    if (Array.isArray(list) && list.length) categories.value = list
  } catch { /* keep fallback */ }
}

// Categories ordered parent-then-children for the multi-pickers (shoot + image).
const categoryOptions = computed<any[]>(() => {
  const cats = categories.value as any[]
  if (!cats.some(c => 'parent_id' in c)) return cats // flat fallback list
  const tops = cats.filter(c => c.parent_id == null)
  const out: any[] = []
  for (const t of tops) {
    out.push(t)
    for (const ch of cats.filter(c => c.parent_id === t.id)) out.push(ch)
  }
  // Any orphan children (parent filtered out) still appear.
  for (const c of cats) if (!out.includes(c)) out.push(c)
  return out
})

// Multi-category selection for the shoot form and the image form. First slug =
// primary. Toggling is handled by the shared CategoryPicker component.
const shootCats = ref<string[]>([])
const imgCats = ref<string[]>([])
const newCategory = reactive<{ active: boolean; label: string; status: string }>({ active: false, label: '', status: '' })
function startNewCategory() { newCategory.active = true; newCategory.label = ''; newCategory.status = '' }
async function saveNewCategory() {
  const label = newCategory.label.trim()
  if (!label) return
  newCategory.status = 'Creating…'
  try {
    const res = await apiFetch('/admin/portfolio/categories', { method: 'POST', body: JSON.stringify({ label }) })
    const r = await res.json().catch(() => ({}))
    if (res.ok && r.slug) {
      await loadCategories()
      shootForm.category = r.slug
      newCategory.active = false
      newCategory.status = ''
    } else {
      // 409 = case-insensitive duplicate (slug or label); show the server's message.
      newCategory.status = r.error || (res.status === 409 ? 'That category already exists.' : 'Failed.')
    }
  } catch { newCategory.status = 'Failed.' }
}

// Web variants are served under this CloudFront prefix (Slice C wires the behaviour;
// until then the frontend bucket path still resolves for preview).
const MEDIA_PREFIX = '/portfolio-media/'

const tab = ref<'shoots' | 'home'>('shoots')

// ── Shoots ──
const shoots = ref<any[]>([])
const loadingShoots = ref(false)
const shootQuery = ref('')
const selectedShoot = ref<any | null>(null)
const editingShoot = ref(false)
const shootForm = reactive<any>({ id: null, name: '', category: null, slug: '', shootDate: '',
  locationId: null, locationVenue: '', locationAddress: '', locationCity: '', locationState: '', notes: '' })

// Saved locations (first-class entities). The shoot can pick an existing one
// (sets locationId + fills the display fields) or type a fresh location inline.
const savedLocations = ref<any[]>([])
async function loadLocations() {
  try {
    const res = await apiFetch('/admin/portfolio/locations')
    savedLocations.value = (await res.json()).locations || []
  } catch { savedLocations.value = [] }
}
function onPickLocation() {
  const id = shootForm.locationId
  if (!id) return
  const l = savedLocations.value.find(x => x.id === id)
  if (l) {
    shootForm.locationVenue = l.location_venue || ''
    shootForm.locationAddress = l.location_address || ''
    shootForm.locationCity = l.location_city || ''
    shootForm.locationState = l.location_state || ''
  }
}

// Shoot-form location SEARCH (replaces the old dropdown — dropdowns don't scale).
// Picking a saved location sets locationId + fills the display fields; clearing
// it drops back to a typed one-off (locationId null, fields editable below).
const shootLocQuery = ref('')
const shootLocResults = ref<any[]>([])
const shootLocPickerOpen = ref(false)
let shootLocTimer: any = null
function onShootLocationSearch() {
  const q = shootLocQuery.value.trim()
  clearTimeout(shootLocTimer)
  shootLocTimer = setTimeout(async () => {
    try {
      const res = await apiFetch(`/admin/portfolio/locations?q=${encodeURIComponent(q)}`)
      shootLocResults.value = (await res.json()).locations || []
    } catch { shootLocResults.value = [] }
  }, 200)
}
function shootLocBlur() { setTimeout(() => { shootLocPickerOpen.value = false }, 150) }
function pickShootLocation(l: any) {
  shootForm.locationId = l.id
  shootForm.locationVenue = l.location_venue || ''
  shootForm.locationAddress = l.location_address || ''
  shootForm.locationCity = l.location_city || ''
  shootForm.locationState = l.location_state || ''
  shootLocResults.value = []
  shootLocPickerOpen.value = false
}
function clearShootLocation() {
  shootForm.locationId = null
  shootForm.locationVenue = ''
  shootForm.locationAddress = ''
  shootForm.locationCity = ''
  shootForm.locationState = ''
  shootLocQuery.value = ''
}
function locLabel(l: any): string {
  return [l.location_venue, l.location_city, l.location_state].filter(Boolean).join(' · ') || l.location_address || `#${l.id}`
}

// Credit roles someone can fill ON A SHOOT (not an attribute of the person).
const CREDIT_ROLE_OPTIONS = [
  { value: 'model',       label: 'Model' },
  { value: 'hair_makeup', label: 'Hair & Makeup' },
  { value: 'hair',        label: 'Hair' },
  { value: 'makeup',      label: 'Makeup' },
  { value: 'stylist',     label: 'Stylist' },
  { value: 'assistant',   label: 'Assistant' },
  { value: 'other',       label: 'Other' },
]
// A shoot credit = a contributor (model/crew) + the role they filled here.
// contributorId links to an existing person; name alone find-or-creates on save.
type Credit = { contributorId: number | null; name: string; role: string }
const shootCredits = ref<Credit[]>([])
function addCredit() { shootCredits.value.push({ contributorId: null, name: '', role: 'model' }) }
function removeCredit(i: number) { shootCredits.value.splice(i, 1) }

// Contributor autocomplete (search people you've worked with to avoid duplicates).
const contribActiveRow = ref<number | null>(null)
const contribResults = ref<any[]>([])
let contribSearchTimer: any = null
function onContributorSearch(cr: Credit, i: number) {
  contribActiveRow.value = i
  cr.contributorId = null // editing the name unlinks until re-picked
  const q = cr.name.trim()
  clearTimeout(contribSearchTimer)
  contribSearchTimer = setTimeout(async () => {
    try {
      const res = await apiFetch(`/admin/portfolio/contributors?q=${encodeURIComponent(q)}`)
      contribResults.value = (await res.json()).contributors || []
    } catch { contribResults.value = [] }
  }, 200)
}
function pickContributor(cr: Credit, r: any) {
  cr.contributorId = r.id
  cr.name = r.name
  contribResults.value = []
  contribActiveRow.value = null
}
function fmtYear(d: string) { return d ? String(d).slice(0, 4) : '' }
function shootLocation(s: any): string {
  return [s?.location_venue, s?.location_address, s?.location_city, s?.location_state].filter(Boolean).join(', ')
}
function roleLabel(role: string): string {
  return CREDIT_ROLE_OPTIONS.find(r => r.value === role)?.label || role
}

// Library photos live under library/{people,locations}/… at the CloudFront root,
// a different prefix from portfolio media — serve them straight off the key.
// photoUrl is shared via composables/useAdminApi.

// ── Read-only info modals: click the shoot's location or a credit to see details.
const infoLoc = reactive<{ open: boolean; loading: boolean; found: boolean; data: any }>({ open: false, loading: false, found: false, data: {} })
async function openLocationModal(shoot: any) {
  infoLoc.open = true; infoLoc.loading = true; infoLoc.found = false
  // Shoot's own fields are the baseline (covers one-off locations with no saved row).
  infoLoc.data = {
    venue: shoot?.location_venue || '', address: shoot?.location_address || '',
    city: shoot?.location_city || '', state: shoot?.location_state || '',
    url: '', email: '', socials: [], notes: '', is_closed: false,
  }
  const id = shoot?.location_id
  if (!id) { infoLoc.loading = false; return }
  try {
    const res = await apiFetch(`/admin/portfolio/locations?id=${id}`)
    if (res.ok) {
      const l = (await res.json()).location
      if (l) {
        infoLoc.found = true
        infoLoc.data = {
          venue: l.location_venue || l.venue || infoLoc.data.venue,
          address: l.location_address || l.address || infoLoc.data.address,
          city: l.location_city || l.city || infoLoc.data.city,
          state: l.location_state || l.state || infoLoc.data.state,
          url: l.url || '', email: l.email || '',
          socials: l.socials || [], notes: l.notes || '', is_closed: !!l.is_closed,
        }
      }
    }
  } catch { /* fall back to shoot fields */ }
  infoLoc.loading = false
}

const infoContrib = reactive<{ open: boolean; loading: boolean; found: boolean; roleHere: string; data: any }>({ open: false, loading: false, found: false, roleHere: '', data: {} })
async function openContributorModal(credit: any) {
  infoContrib.open = true; infoContrib.loading = true; infoContrib.found = false
  infoContrib.roleHere = roleLabel(credit?.role || '')
  infoContrib.data = { name: credit?.name || '', socials: [], primary_roles: [] }
  const id = credit?.contributorId ?? credit?.contributor_id
  if (!id) { infoContrib.loading = false; return }
  try {
    const res = await apiFetch(`/admin/portfolio/contributors?id=${id}`)
    if (res.ok) {
      const c = (await res.json()).contributor
      if (c) { infoContrib.found = true; infoContrib.data = c }
    }
  } catch { /* fall back to credit name */ }
  infoContrib.loading = false
}

// Contributor editor (name/business/notes + multi-social).
const contribEdit = reactive<{ active: boolean; id: number | null; name: string; businessName: string; notes: string; socials: Array<{ platform: string; url: string }>; status: string; forRow: Credit | null }>(
  { active: false, id: null, name: '', businessName: '', notes: '', socials: [], status: '', forRow: null }
)
async function editContributor(cr: Credit) {
  contribEdit.active = true
  contribEdit.status = ''
  contribEdit.forRow = cr
  if (cr.contributorId) {
    const res = await apiFetch(`/admin/portfolio/contributors?id=${cr.contributorId}`)
    const c = (await res.json()).contributor
    Object.assign(contribEdit, {
      id: c.id, name: c.name, businessName: c.business_name || '', notes: c.notes || '',
      socials: (c.socials || []).map((s: any) => ({ platform: s.platform, url: s.url })),
    })
  } else {
    Object.assign(contribEdit, { id: null, name: cr.name.trim(), businessName: '', notes: '', socials: [] })
  }
}
// addSocial removed — SocialLinksEditor owns add/remove.
async function saveContributor() {
  if (!contribEdit.name.trim()) { contribEdit.status = 'Name required.'; return }
  contribEdit.status = 'Saving…'
  try {
    const res = await apiFetch('/admin/portfolio/contributors', {
      method: 'POST',
      body: JSON.stringify({
        id: contribEdit.id, name: contribEdit.name.trim(), businessName: contribEdit.businessName.trim() || null,
        notes: contribEdit.notes.trim() || null,
        socials: contribEdit.socials.filter(s => s.platform && s.url.trim()),
      }),
    })
    const r = await res.json()
    if (r.id) {
      // Link the credit row to the saved contributor.
      if (contribEdit.forRow) { contribEdit.forRow.contributorId = r.id; contribEdit.forRow.name = contribEdit.name.trim() }
      contribEdit.status = 'Saved.'
      contribEdit.active = false
    } else { contribEdit.status = r.error || 'Failed.' }
  } catch { contribEdit.status = 'Failed.' }
}

// Full-screen lightbox: prefer the largest variant available for a crisp full view.
const lightboxSrc = ref<string | null>(null)
function lightboxUrl(img: any): string | undefined {
  const v = variantByLabel(img, 'full') || variantByLabel(img, '4k') || variantByLabel(img, '2150')
    || variantByLabel(img, '900') || variantByLabel(img, '600') || img?.variants?.[0]
  return variantSrc(v)
}
function openLightbox(img: any) {
  const url = lightboxUrl(img)
  if (url) lightboxSrc.value = url
}

async function loadShoots() {
  loadingShoots.value = true
  try {
    const q = (shootQuery.value || pickerQuery.value || '').trim()
    const res = await apiFetch(`/admin/shoots${q ? `?q=${encodeURIComponent(q)}` : ''}`)
    shoots.value = (await res.json()).shoots || []
  } finally { loadingShoots.value = false }
}

function startNewShoot() {
  editingShoot.value = true
  Object.assign(shootForm, { id: null, name: '', category: null, slug: '', shootDate: '',
    locationId: null, locationVenue: '', locationAddress: '', locationCity: '', locationState: '', notes: '' })
  shootCredits.value = []
  contribEdit.active = false
  shootCats.value = []
}
function editShoot(s: any) {
  editingShoot.value = true
  Object.assign(shootForm, {
    id: s.id, name: s.name, category: s.category, slug: s.slug,
    shootDate: s.shoot_date ? s.shoot_date.slice(0, 10) : '',
    locationId: s.location_id ?? null,
    locationVenue: s.location_venue || '', locationAddress: s.location_address || '', locationCity: s.location_city || '', locationState: s.location_state || '',
    notes: s.notes || '',
  })
  // Multi-category: prefer freshly-loaded detail set; fall back to the single
  // legacy category so an unmigrated/legacy shoot still shows its tag.
  const cats = (selectedShoot.value?.id === s.id ? selectedShoot.value?.categories : s.categories)
  shootCats.value = Array.isArray(cats) && cats.length ? [...cats] : (s.category ? [s.category] : [])
  // Prefer freshly-loaded detail credits; fall back to the list row if present.
  const src = (selectedShoot.value?.id === s.id ? selectedShoot.value?.credits : s.credits) || []
  shootCredits.value = src.map((c: any) => ({
    contributorId: c.contributorId ?? c.contributor_id ?? null,
    name: c.name || '', role: c.role || 'model',
  }))
  contribEdit.active = false
}
function cancelShootEdit() { editingShoot.value = false }

async function saveShoot() {
  if (!shootForm.name.trim()) return
  const credits = shootCredits.value
    .filter(c => c.role && (c.contributorId || c.name.trim()))
    .map(c => ({ contributorId: c.contributorId, name: c.name.trim() || undefined, role: c.role }))
  const body = JSON.stringify({
    name: shootForm.name, category: shootCats.value[0] || shootForm.category || null, slug: shootForm.slug || undefined,
    shootDate: shootForm.shootDate || null,
    locationId: shootForm.locationId || null,
    locationVenue: shootForm.locationVenue || null, locationAddress: shootForm.locationAddress || null, locationCity: shootForm.locationCity || null, locationState: shootForm.locationState || null,
    notes: shootForm.notes || null,
    credits,
    categories: shootCats.value,
  })
  if (shootForm.id) {
    await apiFetch(`/admin/shoots/${shootForm.id}`, { method: 'PATCH', body })
  } else {
    const res = await apiFetch('/admin/shoots', { method: 'POST', body })
    const { shootId } = await res.json()
    editingShoot.value = false
    await loadShoots(); await selectShoot(shootId); return
  }
  editingShoot.value = false
  await loadShoots()
  if (selectedShoot.value) await selectShoot(selectedShoot.value.id)
}

async function selectShoot(id: number) {
  // Clear multi-select only on a real shoot switch, not an in-place refresh.
  if (selectedShoot.value?.id !== id) { selectedIds.value = new Set(); bulkDeleteConfirm.value = false }
  editingShoot.value = false
  detailImage.value = null
  const res = await apiFetch(`/admin/shoots/${id}`)
  selectedShoot.value = (await res.json()).shoot
  maybePollVariants()
}

// ── Variant generation status ────────────────────────────────────────────────
// SHARED source of truth: image sizes + the per-label format rule live in the
// backend config and are imported here via the @shared alias, so "complete"
// means exactly what the resize Lambda produces. No local mirror to drift.
function expectedPairs(img: any): string[] {
  const orientation = img?.orientation === 'portrait' ? 'portrait' : 'landscape'
  return expectedVariantKeys(orientation)
}
// Normalize a variant to the shared "label:format" key (jpeg → jpg).
function variantKey(v: any): string {
  return `${v?.label}:${normalizeFormat(v?.format)}`
}
function expectedVariantCount(img: any): number {
  return expectedPairs(img).length
}
// How many of the REQUIRED pairs are present (extras like on-demand full.avif
// are ignored, so they never inflate past the expected total).
function presentRequiredCount(img: any): number {
  const have = new Set((img?.variants || []).map(variantKey))
  return expectedPairs(img).filter(p => have.has(p)).length
}
function variantStatus(img: any): 'none' | 'generating' | 'complete' {
  const total = (img?.variants?.length || 0)
  if (total === 0) return 'none'
  // Complete = every required (label, format) pair exists — not a raw count.
  return presentRequiredCount(img) >= expectedVariantCount(img) ? 'complete' : 'generating'
}
function anyGenerating(): boolean {
  return (selectedShoot.value?.images || []).some((i: any) => {
    const s = variantStatus(i)
    // 'none' right after upload also means "generation pending/in flight".
    return s === 'generating' || s === 'none'
  })
}

// Re-fetch the shoot WITHOUT tearing down the open detail drawer, so variants
// appear as they land. Preserves the selected image by id.
async function refreshShoot() {
  if (!selectedShoot.value) return
  const openId = detailImage.value?.id
  const res = await apiFetch(`/admin/shoots/${selectedShoot.value.id}`)
  selectedShoot.value = (await res.json()).shoot
  if (openId) {
    const again = (selectedShoot.value.images || []).find((i: any) => i.id === openId)
    if (again) detailImage.value = again
  }
}

// ── Drag-to-reorder thumbnails ───────────────────────────────────────────────
// HTML5 drag/drop on the grid. On drop we reorder the in-memory array for an
// instant visual, renumber sort_order 0..N-1, then persist only the rows whose
// position actually changed via the existing PATCH {sortOrder} endpoint. The
// backend already orders getShootDetail by (sort_order, id), so a refresh keeps
// the new order. A plain click still opens the detail drawer (drag is distinct).
const dragFromIdx = ref<number | null>(null)
const dragOverIdx = ref<number | null>(null)
function onDragStart(idx: number, e: DragEvent) {
  dragFromIdx.value = idx
  if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)) }
}
function onDragOver(idx: number) { if (dragFromIdx.value !== null && idx !== dragFromIdx.value) dragOverIdx.value = idx }
function onDragLeave(idx: number) { if (dragOverIdx.value === idx) dragOverIdx.value = null }
function onDragEnd() { dragFromIdx.value = null; dragOverIdx.value = null }
async function onDrop(toIdx: number) {
  const from = dragFromIdx.value
  dragOverIdx.value = null
  dragFromIdx.value = null
  if (from === null || from === toIdx || !selectedShoot.value) return
  const imgs = [...(selectedShoot.value.images || [])]
  const [moved] = imgs.splice(from, 1)
  imgs.splice(toIdx, 0, moved)
  // Capture the old positions so we only PATCH the rows that actually moved.
  const prevOrder = new Map<number, number>()
  ;(selectedShoot.value.images || []).forEach((im: any, i: number) => prevOrder.set(im.id, i))
  selectedShoot.value.images = imgs   // instant visual reorder
  const changed = imgs.filter((im: any, i: number) => prevOrder.get(im.id) !== i)
  if (!changed.length) return
  saveMetaStatus.value = 'Saving order…'
  try {
    await Promise.all(imgs.map((im: any, i: number) => {
      if (prevOrder.get(im.id) === i) return Promise.resolve()
      im.sort_order = i
      return apiFetch(`/admin/shoots/${selectedShoot.value!.id}/images/${im.id}`, {
        method: 'PATCH', body: JSON.stringify({ sortOrder: i }),
      })
    }))
    saveMetaStatus.value = 'Order saved ✓'
    setTimeout(() => { if (saveMetaStatus.value === 'Order saved ✓') saveMetaStatus.value = '' }, 2000)
  } catch {
    saveMetaStatus.value = 'Order save failed — refreshing'
    await refreshShoot()
  }
}

// Poll a few times while anything is still generating, then stop. Cheap: one
// GET per tick, self-cancelling once everything is complete.
let variantPollTimer: any = null
function maybePollVariants() {
  if (variantPollTimer) { clearTimeout(variantPollTimer); variantPollTimer = null }
  if (!anyGenerating()) return
  let ticks = 0
  const tick = async () => {
    ticks++
    await refreshShoot()
    if (anyGenerating() && ticks < 20) {   // up to ~20 ticks (≈60s at 3s)
      variantPollTimer = setTimeout(tick, 3000)
    } else {
      variantPollTimer = null
    }
  }
  variantPollTimer = setTimeout(tick, 3000)
}

// ── Bulk image upload (files, multi-select, drag-drop, whole folder) ──
const uploadStatus = ref('')
const isDragging = ref(false)
const uploadQueue = ref<File[]>([])
const uploadDone = ref(0)
const uploadFailed = ref(0)
const uploadSkipped = ref(0)
const uploadPct = ref(0)

// SHA-256 (hex) of a file's bytes, for same-shoot duplicate detection.
// Uses the native SubtleCrypto digest — no dependency, runs in the browser.
async function sha256Hex(file: File): Promise<string | null> {
  try {
    const buf = await file.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return null // hashing failed (e.g. non-secure context) → skip dedupe, upload normally
  }
}

// Duplicate-prompt state. When the backend reports a byte-identical image
// already in this shoot (409), we pause the queue and ask the user.
// `applyAll` lets one choice apply to every remaining duplicate in the batch.
const dupePrompt = ref<{
  open: boolean
  file: File | null
  existing: { imageId: number; displayName: string | null } | null
  resolve: ((choice: 'overwrite' | 'add' | 'skip') => void) | null
}>({ open: false, file: null, existing: null, resolve: null })
// Remembered "apply to all remaining" decision for the current batch.
const dupeApplyAll = ref<'overwrite' | 'add' | 'skip' | null>(null)
// Bound to the "apply to all remaining" checkbox in the prompt.
const dupeAllChecked = ref(false)

function resolveDupe(choice: 'overwrite' | 'add' | 'skip', applyAll = false) {
  if (applyAll) dupeApplyAll.value = choice
  dupeAllChecked.value = false
  const r = dupePrompt.value.resolve
  dupePrompt.value = { open: false, file: null, existing: null, resolve: null }
  r?.(choice)
}

/**
 * Create one shoot image. Computes the content hash and sends it. If the
 * backend reports a same-shoot duplicate (409), ask the user (or apply the
 * batch-wide decision) and either overwrite, add-anyway, or skip.
 * Returns 'uploaded' | 'skipped'.
 */
async function uploadOneFile(shootId: number, file: File): Promise<'uploaded' | 'skipped'> {
  const contentType = file.type || 'image/jpeg'
  const dims = await imageDims(file)
  const contentHash = await sha256Hex(file)
  const shoot = selectedShoot.value
  const inheritTakenDate = shoot?.shoot_date ? String(shoot.shoot_date).slice(0, 10) : null
  const inheritCategory = shoot?.category || null

  const baseBody = {
    filename: file.name, contentType,
    width: dims.w, height: dims.h, bytes: file.size,
    displayName: file.name.replace(/\.[^.]+$/, ''),
    takenDate: inheritTakenDate,
    category: inheritCategory,
    label: 'full',
    contentHash,
  }

  // First attempt: no dupeMode → backend returns 409 if a same-hash image exists.
  let dupeMode: 'overwrite' | 'add' | undefined
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await apiFetch(`/admin/shoots/${shootId}/images`, {
      method: 'POST',
      body: JSON.stringify({ ...baseBody, ...(dupeMode ? { dupeMode } : {}) }),
    })

    if (res.status === 409 && contentHash) {
      const { duplicate } = await res.json().catch(() => ({ duplicate: null }))
      // Decide: batch-wide decision if set, else prompt.
      let choice = dupeApplyAll.value
      if (!choice) {
        choice = await new Promise<'overwrite' | 'add' | 'skip'>((resolve) => {
          dupePrompt.value = { open: true, file, existing: duplicate, resolve }
        })
      }
      if (choice === 'skip') return 'skipped'
      dupeMode = choice === 'overwrite' ? 'overwrite' : 'add'
      continue // retry with the chosen mode
    }

    if (!res.ok) throw new Error(`create failed: ${res.status}`)
    const created = await res.json()
    // PUT the file to the presigned url the create returned.
    await fetch(created.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
    // Fan out AVIF + WebP + JPG (JPG is always generated; no alsoJpg flag).
    await apiFetch(`/admin/shoots/${shootId}/images/${created.imageId}/generate`, { method: 'POST' })
    return 'uploaded'
  }
}

async function runUploadQueue(files: File[]) {
  if (!selectedShoot.value || !files.length) return
  const shootId = selectedShoot.value.id
  // Only real images (a dropped folder may include non-images).
  const imgs = files.filter(f => f.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|tiff?|bmp)$/i.test(f.name))
  uploadQueue.value = imgs
  uploadDone.value = 0; uploadFailed.value = 0; uploadSkipped.value = 0; uploadPct.value = 0
  dupeApplyAll.value = null // fresh batch decision
  uploadStatus.value = `Uploading ${imgs.length} image${imgs.length === 1 ? '' : 's'}…`
  // Sequential to keep memory + presign rate sane for large folders (and so the
  // duplicate prompt pauses the whole queue, not just one of N parallel uploads).
  for (const file of imgs) {
    try {
      const outcome = await uploadOneFile(shootId, file)
      if (outcome === 'skipped') uploadSkipped.value++
    }
    catch (err) { uploadFailed.value++; console.error('upload failed:', file.name, err) }
    uploadDone.value++
    uploadPct.value = Math.round((uploadDone.value / imgs.length) * 100)
  }
  const uploaded = uploadDone.value - uploadFailed.value - uploadSkipped.value
  const parts = [`${uploaded} uploaded`]
  if (uploadSkipped.value) parts.push(`${uploadSkipped.value} skipped (duplicate)`)
  if (uploadFailed.value) parts.push(`${uploadFailed.value} failed (see console)`)
  uploadStatus.value = `Done: ${parts.join(', ')}.`
  uploadQueue.value = []
  dupeApplyAll.value = null
  await selectShoot(shootId)
  setTimeout(() => { uploadStatus.value = '' }, 5000)
}

function onUploadFiles(e: Event) {
  const files = Array.from((e.target as HTMLInputElement).files || [])
  ;(e.target as HTMLInputElement).value = '' // allow re-selecting the same set
  runUploadQueue(files)
}

async function onDropFiles(e: DragEvent) {
  isDragging.value = false
  const items = e.dataTransfer?.items
  const files: File[] = []
  if (items && items.length && (items[0] as any).webkitGetAsEntry) {
    // Walk dropped directory trees (webkit entries) so a dropped FOLDER works.
    const entries = Array.from(items).map(it => (it as any).webkitGetAsEntry()).filter(Boolean)
    for (const entry of entries) await walkEntry(entry, files)
  } else {
    for (const f of Array.from(e.dataTransfer?.files || [])) files.push(f)
  }
  runUploadQueue(files)
}

function walkEntry(entry: any, out: File[]): Promise<void> {
  return new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((f: File) => { out.push(f); resolve() }, () => resolve())
    } else if (entry.isDirectory) {
      const reader = entry.createReader()
      reader.readEntries(async (ents: any[]) => {
        for (const e of ents) await walkEntry(e, out)
        resolve()
      }, () => resolve())
    } else resolve()
  })
}

// ── RAW folder matcher ───────────────────────────────────────────────────────
// Browser can't read an arbitrary local path, so the user picks the RAW folder
// via the OS dialog (webkitdirectory). We match each shoot photo's base name to
// a RAW file in that folder, then upload+attach the matched RAWs.
const RAW_EXT_RE = /\.(nef|cr2|cr3|arw|dng|raf|orf|rw2|pef|srw|tiff?|raw)$/i

// Preference when several files share one capture base: a true camera RAW
// (NEF/CR2/…) beats a DNG, which beats a TIFF or generic .raw export. Lower
// rank wins. Unknown/non-RAW extensions sort last.
const RAW_FORMAT_ORDER = ['nef', 'cr2', 'cr3', 'arw', 'raf', 'orf', 'rw2', 'pef', 'srw', 'dng', 'tif', 'tiff', 'raw']
function rawFormatRank(name: string): number {
  const ext = (name.match(/\.([^.]+)$/)?.[1] || '').toLowerCase()
  const i = RAW_FORMAT_ORDER.indexOf(ext)
  return i === -1 ? RAW_FORMAT_ORDER.length : i
}

interface RawMatchRow { imageId: number; displayName: string; rawName: string; file: File; alreadyHasRaw: boolean }
const rawMatch = reactive<{
  open: boolean; busy: boolean; folderName: string; rawFileCount: number;
  matched: RawMatchRow[]; unmatchedImages: string[]; done: number; status: string;
}>({ open: false, busy: false, folderName: '', rawFileCount: 0, matched: [], unmatchedImages: [], done: 0, status: '' })

// Normalize a name to its capture base: drop extension, strip trailing
// -Edit / -Edit-Edit / … (and common export suffixes), lowercase.
function captureBase(name: string): string {
  const noExt = name.replace(/\.[^.]+$/, '')
  return noExt
    .replace(/(?:[-_](?:edit|edited|final|retouch(?:ed)?|export|copy|web))+$/i, '')
    .trim()
    .toLowerCase()
}

function closeRawMatch() {
  if (rawMatch.busy) return
  rawMatch.open = false
  rawMatch.matched = []
  rawMatch.unmatchedImages = []
  rawMatch.folderName = ''
  rawMatch.rawFileCount = 0
  rawMatch.done = 0
  rawMatch.status = ''
}

function onPickRawFolder(e: Event) {
  const input = e.target as HTMLInputElement
  const all = Array.from(input.files || [])
  input.value = '' // allow re-picking the same folder
  if (!selectedShoot.value || !all.length) return

  const rawFiles = all.filter(f => RAW_EXT_RE.test(f.name))
  rawMatch.folderName = (all[0] as any).webkitRelativePath?.split('/')[0] || ''
  rawMatch.rawFileCount = rawFiles.length

  // Index RAWs by capture base. When two files share a base (e.g. a NEF and a
  // TIFF export of the same capture), prefer a true camera RAW over a TIFF/RAW
  // fallback — rank by format, then by name for determinism within a rank.
  const byBase = new Map<string, File>()
  for (const f of [...rawFiles].sort((a, b) => {
    const r = rawFormatRank(a.name) - rawFormatRank(b.name)
    return r !== 0 ? r : a.name.localeCompare(b.name)
  })) {
    const base = captureBase(f.name)
    if (!byBase.has(base)) byBase.set(base, f)
  }

  const matched: RawMatchRow[] = []
  const unmatched: string[] = []
  for (const img of (selectedShoot.value.images || [])) {
    const dn = img.display_name || String(img.id)
    const file = byBase.get(captureBase(dn))
    if (file) matched.push({ imageId: img.id, displayName: dn, rawName: file.name, file, alreadyHasRaw: !!img.raw_s3_key })
    else unmatched.push(dn)
  }

  rawMatch.matched = matched
  rawMatch.unmatchedImages = unmatched
  rawMatch.done = 0
  rawMatch.status = rawFiles.length ? '' : 'No RAW files (.NEF/.CR2/.ARW/.DNG/…) found in that folder.'
  rawMatch.open = true
}

async function commitRawMatch() {
  if (!selectedShoot.value || !rawMatch.matched.length || rawMatch.busy) return
  const shootId = selectedShoot.value.id
  rawMatch.busy = true
  rawMatch.done = 0
  let failed = 0
  for (const row of rawMatch.matched) {
    try {
      // 1) presigned PUT into the private bucket (RAW path)
      const pres = await (await apiFetch(`/admin/shoots/${shootId}/images/upload-url?type=raw`, {
        method: 'POST',
        body: JSON.stringify({ filename: row.rawName, contentType: 'application/octet-stream' }),
      })).json()
      await fetch(pres.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: row.file })
      // 2) attach the RAW key to the image row
      await apiFetch(`/admin/shoots/${shootId}/images/${row.imageId}`, {
        method: 'PATCH',
        body: JSON.stringify({ rawS3Key: pres.s3Key }),
      })
    } catch (err) {
      failed++
      console.error('RAW attach failed:', row.rawName, err)
    }
    rawMatch.done++
    rawMatch.status = `Uploaded ${rawMatch.done}/${rawMatch.matched.length}${failed ? ` · ${failed} failed` : ''}`
  }
  rawMatch.busy = false
  const ok = rawMatch.done - failed
  rawMatch.status = failed ? `Attached ${ok} RAW${ok === 1 ? '' : 's'}, ${failed} failed (see console).` : `Attached ${ok} RAW${ok === 1 ? '' : 's'}.`
  await refreshShoot()
  if (!failed) setTimeout(() => { if (!rawMatch.busy) closeRawMatch() }, 2500)
}

const detailImage = ref<any | null>(null)
const deleteConfirm = ref(false)
const imgForm = reactive<any>({ displayName: '', title: '', category: null, takenDate: '', altText: '', locationText: '', peopleText: '', captureGroup: '' })

// Image People picker: chips of {id?, name}. Managed by the shared PeoplePicker
// component (v-model=imgPeople); a typed name with no id is a free-text person.
const imgPeople = ref<Array<{ id: number | null; name: string }>>([])

function variantByLabel(img: any, label: string) {
  return img?.variants?.find((v: any) => v.label === label)
}
function variantSrc(v: any): string | undefined {
  return v?.s3_key ? MEDIA_PREFIX + v.s3_key.replace(/^portfolio-media\//, '') : undefined
}
function variantUrl(v: any): string | undefined {
  return variantSrc(v)
}
function thumbUrl(img: any): string | undefined {
  const v = variantByLabel(img, '600') || variantByLabel(img, '900') || variantByLabel(img, 'full') || img?.variants?.[0]
  return variantSrc(v)
}
const feedThumb = thumbUrl

function imageDims(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const im = new Image()
    im.onload = () => { resolve({ w: im.naturalWidth, h: im.naturalHeight }); URL.revokeObjectURL(url) }
    im.onerror = () => { resolve({ w: 0, h: 0 }); URL.revokeObjectURL(url) }
    im.src = url
  })
}

function openImage(img: any) {
  detailImage.value = img
  deleteConfirm.value = false
  Object.assign(imgForm, {
    displayName: img.display_name || '', title: img.title || '', category: img.category || null,
    takenDate: img.taken_date ? img.taken_date.slice(0, 10) : '', altText: img.alt_text || '',
    locationText: img.location_text || '', peopleText: (img.people || []).map((p: any) => p.name).join(', '),
    captureGroup: img.capture_group || '',
  })
  // People picker chips, from the image's linked people.
  imgPeople.value = (img.people || []).map((p: any) => ({ id: p.id ?? p.contributor_id ?? null, name: p.name }))
  // Multi-category chips, from the image's set (fall back to its single category).
  imgCats.value = Array.isArray(img.categories) && img.categories.length ? [...img.categories] : (img.category ? [img.category] : [])
}

async function saveImageMeta() {
  if (!detailImage.value || !selectedShoot.value) return
  // imgPeople is the source of truth; PeoplePicker commits typed names into it.
  const people = imgPeople.value.map(p => p.name.trim()).filter(Boolean)
  saveMetaStatus.value = 'Saving…'
  await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${detailImage.value.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      displayName: imgForm.displayName, title: imgForm.title, category: imgCats.value[0] || imgForm.category || null,
      takenDate: imgForm.takenDate || null, altText: imgForm.altText, locationText: imgForm.locationText, people,
      captureGroup: imgForm.captureGroup.trim() || null,
      categories: imgCats.value,
    }),
  })
  // Refresh in place — keep the drawer open on the same image, just update its row.
  await refreshShoot()
  saveMetaStatus.value = 'Saved ✓'
  setTimeout(() => { if (saveMetaStatus.value === 'Saved ✓') saveMetaStatus.value = '' }, 2000)
}

const genStatus = ref('')
const saveMetaStatus = ref('')
async function generateVariants() {
  if (!detailImage.value || !selectedShoot.value) return
  genStatus.value = 'Generating variants… this updates automatically as they finish.'
  await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${detailImage.value.id}/generate`, {
    method: 'POST',
  })
  maybePollVariants()
}
async function generateFullFormats() {
  if (!detailImage.value || !selectedShoot.value) return
  genStatus.value = 'Generating full-resolution WebP/AVIF… slower + larger; updates automatically.'
  await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${detailImage.value.id}/generate`, {
    method: 'POST', body: JSON.stringify({ fullFormats: true }),
  })
  maybePollVariants()
}

async function onUploadRaw(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file || !detailImage.value || !selectedShoot.value) return
  genStatus.value = 'Uploading RAW…'
  const shootId = selectedShoot.value.id
  const pres = await (await apiFetch(`/admin/shoots/${shootId}/images/upload-url?type=raw`, {
    method: 'POST', body: JSON.stringify({ filename: file.name, contentType: file.type || 'application/octet-stream' }),
  })).json()
  await fetch(pres.uploadUrl, { method: 'PUT', body: file })
  await apiFetch(`/admin/shoots/${shootId}/images/${detailImage.value.id}`, {
    method: 'PATCH', body: JSON.stringify({ rawS3Key: pres.s3Key }),
  })
  genStatus.value = 'RAW stored.'
  await selectShoot(shootId)
  detailImage.value = selectedShoot.value.images.find((i: any) => i.id === detailImage.value.id) || detailImage.value
}

async function downloadRaw() {
  if (!detailImage.value || !selectedShoot.value) return
  const res = await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${detailImage.value.id}/raw-url`)
  const { url } = await res.json()
  if (url) window.open(url, '_blank')
}

async function deleteVariant(vid: number) {
  if (!detailImage.value || !selectedShoot.value) return
  await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${detailImage.value.id}/variants/${vid}`, { method: 'DELETE' })
  await selectShoot(selectedShoot.value.id)
  detailImage.value = selectedShoot.value.images.find((i: any) => i.id === detailImage.value.id) || null
}

async function deleteImage() {
  if (!detailImage.value || !selectedShoot.value) return
  const id = detailImage.value.id
  await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${id}`, { method: 'DELETE' })
  deleteConfirm.value = false
  detailImage.value = null
  await selectShoot(selectedShoot.value.id)
}

// ── Multi-select + bulk operations (thumbnail grid) ──
// Selection is a Set of image ids. A checkbox overlay toggles; a plain click on
// the figure still opens the detail drawer. Bulk ops iterate the existing
// per-image endpoints (no bulk backend route needed), then refresh once.
const selectedIds = ref<Set<number>>(new Set())
const bulkStatus = ref('')
const bulkCategory = ref('')
const bulkTakenDate = ref('')
const bulkBusy = ref(false)
const bulkDeleteConfirm = ref(false)
function isSelected(id: number): boolean { return selectedIds.value.has(id) }
function toggleSelect(id: number) {
  const next = new Set(selectedIds.value)
  next.has(id) ? next.delete(id) : next.add(id)
  selectedIds.value = next
}
function selectAll() {
  selectedIds.value = new Set((selectedShoot.value?.images || []).map((i: any) => i.id))
}
function clearSelection() { selectedIds.value = new Set(); bulkDeleteConfirm.value = false }
const selectedCount = () => selectedIds.value.size
function selectedImages(): any[] {
  const ids = selectedIds.value
  return (selectedShoot.value?.images || []).filter((i: any) => ids.has(i.id))
}
async function runBulk(fn: (img: any) => Promise<void>, verb: string) {
  const imgs = selectedImages()
  if (!imgs.length || !selectedShoot.value) return
  bulkBusy.value = true
  let done = 0, failed = 0
  for (const img of imgs) {
    try { await fn(img); done++ } catch (e) { failed++; console.error(verb, 'failed for', img.id, e) }
    bulkStatus.value = `${verb}: ${done}/${imgs.length}${failed ? ` (${failed} failed)` : ''}…`
  }
  bulkStatus.value = failed ? `${verb} done: ${done - failed} ok, ${failed} failed.` : `${verb} done (${done}).`
  bulkBusy.value = false
  const shootId = selectedShoot.value.id
  await selectShoot(shootId)
  maybePollVariants()
  setTimeout(() => { bulkStatus.value = '' }, 4000)
}
// ── Apply attributes to EVERY photo in the shoot (set shoot-level defaults
// onto all images at once). Each ticked field is applied; unticked = untouched.
const applyAttrs = reactive<{
  open: boolean; busy: boolean; done: number; status: string
  setCategory: boolean; categories: string[]
  setLocation: boolean; locationText: string
  setPeople: boolean
  setTakenDate: boolean; takenDate: string
}>({ open: false, busy: false, done: 0, status: '',
  setCategory: false, categories: [], setLocation: false, locationText: '',
  setPeople: false, setTakenDate: false, takenDate: '' })

// People chips for the Apply-attributes panel (standardized PeoplePicker).
// Serialized to names for the patch in runApplyAttrs.
const applyPeople = ref<Array<{ id: number | null; name: string }>>([])

function openApplyAttrs() {
  const s = selectedShoot.value
  applyPeople.value = []
  Object.assign(applyAttrs, {
    open: true, busy: false, done: 0, status: '',
    setCategory: false, categories: s?.category ? [s.category] : [],
    setLocation: false, locationText: shootLocation(s) || '',
    setPeople: false,
    setTakenDate: false, takenDate: s?.shoot_date ? s.shoot_date.slice(0, 10) : '',
  })
}
function applyAttrsAnySelected(): boolean {
  return applyAttrs.setCategory || applyAttrs.setLocation || applyAttrs.setPeople || applyAttrs.setTakenDate
}
async function runApplyAttrs() {
  const s = selectedShoot.value
  if (!s || !applyAttrsAnySelected()) return
  const imgs = s.images || []
  if (!imgs.length) return
  // Build the shared patch once — same fields go onto every image.
  const patch: any = {}
  if (applyAttrs.setCategory) {
    patch.categories = applyAttrs.categories
    patch.category = applyAttrs.categories[0] || null
  }
  if (applyAttrs.setLocation) patch.locationText = applyAttrs.locationText.trim()
  if (applyAttrs.setPeople) {
    patch.people = applyPeople.value.map(p => p.name.trim()).filter(Boolean)
  }
  if (applyAttrs.setTakenDate) patch.takenDate = applyAttrs.takenDate || null

  applyAttrs.busy = true; applyAttrs.done = 0
  let failed = 0
  for (const img of imgs) {
    try {
      await apiFetch(`/admin/shoots/${s.id}/images/${img.id}`, { method: 'PATCH', body: JSON.stringify(patch) })
    } catch (e) { failed++; console.error('apply-attrs failed for', img.id, e) }
    applyAttrs.done++
    applyAttrs.status = `Applying ${applyAttrs.done}/${imgs.length}${failed ? ` (${failed} failed)` : ''}…`
  }
  applyAttrs.status = failed ? `Done: ${applyAttrs.done - failed} ok, ${failed} failed.` : `Applied to ${applyAttrs.done} photos ✓`
  applyAttrs.busy = false
  await refreshShoot()
  setTimeout(() => { applyAttrs.open = false; applyAttrs.status = '' }, 1800)
}

async function bulkSetCategory() {
  if (!bulkCategory.value) return  await runBulk(async (img) => {
    await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${img.id}`, {
      method: 'PATCH', body: JSON.stringify({ category: bulkCategory.value }),
    })
  }, 'Set category')
}
async function bulkSetTakenDate() {
  if (!bulkTakenDate.value) return
  await runBulk(async (img) => {
    await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${img.id}`, {
      method: 'PATCH', body: JSON.stringify({ takenDate: bulkTakenDate.value }),
    })
  }, 'Set date')
}
async function bulkGenerate() {
  await runBulk(async (img) => {
    await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${img.id}/generate`, { method: 'POST' })
  }, 'Generate')
}
async function bulkDelete() {
  await runBulk(async (img) => {
    await apiFetch(`/admin/shoots/${selectedShoot.value.id}/images/${img.id}`, { method: 'DELETE' })
  }, 'Delete')
  clearSelection()
}

// ── Feed / placement curation (home + category surfaces) ──
const curSurface = ref<string>('home')
const homeFeed = ref<any[]>([])
const feedStatus = ref('')
const dragFrom = ref<number | null>(null)
const dragOver = ref<number | null>(null)
const pickerQuery = ref('')
const pickerOpen = ref<number | null>(null)
const pickerImages = reactive<Record<number, any[]>>({})

async function openHomeFeed() {
  tab.value = 'home'
  await loadShoots()
  await loadSurface()
}
async function loadSurface() {
  const res = await apiFetch(`/admin/portfolio/placements?surface=${encodeURIComponent(curSurface.value)}`)
  homeFeed.value = (await res.json()).images || []
}
function dropAt(i: number) {
  if (dragFrom.value === null || dragFrom.value === i) return
  const arr = homeFeed.value
  const [moved] = arr.splice(dragFrom.value, 1)
  arr.splice(i, 0, moved)
  dragFrom.value = null
  dragOver.value = null
}
function removeFromFeed(i: number) { homeFeed.value.splice(i, 1) }
async function saveHomeFeed() {
  feedStatus.value = 'Saving…'
  await apiFetch('/admin/portfolio/placements', {
    method: 'PUT',
    body: JSON.stringify({ surface: curSurface.value, imageIds: homeFeed.value.map((i) => i.id) }),
  })
  feedStatus.value = 'Saved.'
  setTimeout(() => (feedStatus.value = ''), 1500)
}
async function togglePicker(shootId: number) {
  pickerOpen.value = pickerOpen.value === shootId ? null : shootId
  if (pickerOpen.value && !pickerImages[shootId]) {
    const res = await apiFetch(`/admin/shoots/${shootId}`)
    pickerImages[shootId] = (await res.json()).shoot.images || []
  }
}
function addToFeed(img: any) {
  if (homeFeed.value.some((i) => i.id === img.id)) return
  homeFeed.value.push(img)
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && lightboxSrc.value) lightboxSrc.value = null
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

loadShoots()
loadCategories()
loadLocations()
</script>

<style scoped>
.portfolio-view { padding: 1.5rem 2rem; color: var(--text-primary); }
.pv-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 1.25rem; }
.pv-header h1 { margin: 0; font-size: 1.5rem; }
.pv-sub, .pv-muted { color: var(--text-muted); font-size: 0.85rem; }
.pv-tabs { display: flex; gap: 0.5rem; }
.tab { background: var(--bg-surface); color: var(--text-secondary); border: 1px solid var(--border-default); padding: 0.4rem 0.9rem; border-radius: 6px; cursor: pointer; }
.tab.active { background: var(--accent-dim); color: var(--text-primary); border-color: var(--accent); }

.pv-body { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 1.25rem; align-items: start; }
.home-feed { grid-template-columns: minmax(0, 1fr) 340px; }
.pv-list { min-width: 0; }
.pv-detail { min-width: 0; }

.card { background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 8px; padding: 1.1rem 1.25rem; margin-bottom: 1.1rem;
  /* Override the global App.vue `.card { display:flex }` — in this view a card
     is a vertical container (head → dropzone → grid), not a horizontal row. */
  display: block; }
.card h2 { margin: 0 0 0.75rem; font-size: 1.1rem; }
.card.empty { display: flex; align-items: center; justify-content: center; min-height: 160px; }

.pv-list { }
.pv-list-head { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
.pv-search { flex: 1; background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.45rem 0.6rem; }
.shoot-list { list-style: none; margin: 0; padding: 0; }
.shoot-list li { display: flex; flex-direction: column; gap: 0.25rem; padding: 0.6rem 0.7rem; border-radius: 6px; cursor: pointer; border: 1px solid transparent; }
.shoot-list li:hover { background: var(--bg-surface-hover); }
.shoot-list li.active { background: var(--accent-dim); border-color: var(--accent); }
.shoot-name { font-weight: 600; }
.shoot-meta { display: flex; gap: 0.5rem; align-items: center; }
.chip { font-size: 0.7rem; background: var(--accent-dim); color: var(--text-secondary); padding: 0.1rem 0.45rem; border-radius: 10px; }
.count { font-size: 0.75rem; color: var(--text-muted); }
.slug { font-family: monospace; font-size: 0.75rem; }

.detail-head { display: flex; justify-content: flex-start; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
.detail-head h2, .detail-head h3 { margin: 0; }
.detail-close { flex-shrink: 0; margin-left: auto; }
.form-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.75rem; }
.form-grid label { display: flex; flex-direction: column; font-size: 0.8rem; color: var(--text-secondary); gap: 0.25rem; min-width: 0; }
.form-grid .span2 { grid-column: 1 / -1; }
.form-grid input, .form-grid select, .form-grid textarea { min-width: 0; max-width: 100%; background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.4rem 0.55rem; }
.row-actions { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.9rem; flex-wrap: wrap; }

/* Image-detail card: full-width header band on top; preview + form side by side.
   The preview shows the WHOLE image (contain) and grows with available width. */
.image-detail { overflow: hidden; }
.image-detail-body {
  display: grid;
  grid-template-columns: minmax(240px, 34%) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}
/* Right region: metadata form and variants sit side by side. */
.image-detail-right {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  min-width: 0;
}
.image-detail-preview {
  width: 100%;
  max-height: min(70vh, 640px);
  min-height: 320px;
  background: var(--bg-input);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-detail-preview img { width: 100%; height: 100%; max-height: min(70vh, 640px); object-fit: contain; display: block; }
.image-detail-main { min-width: 0; }
.detail-controls { border-top: 1px solid var(--border-subtle); padding-top: 0.75rem; }
.delete-confirm-label { color: var(--text-secondary); font-size: 0.8rem; }
.variant-link { color: var(--accent-text); text-decoration: underline; }
.variant-link:hover { color: var(--text-primary); }
@media (max-width: 1100px) {
  .image-detail-right { grid-template-columns: 1fr; }
}
@media (max-width: 820px) {
  .image-detail-body { grid-template-columns: 1fr; }
  .image-detail-preview { max-height: 60vh; }
}

.btn-accent { background: var(--accent); color: var(--accent-contrast, #fff); border: none; padding: 0.5rem 0.9rem; border-radius: 6px; cursor: pointer; }
.btn-ghost { background: var(--bg-surface); color: var(--text-secondary); border: 1px solid var(--border-default); padding: 0.5rem 0.9rem; border-radius: 6px; cursor: pointer; }
.btn-danger { background: var(--danger-dim); color: var(--danger-text); border: 1px solid var(--danger); padding: 0.5rem 0.9rem; border-radius: 6px; cursor: pointer; }
.sm { padding: 0.35rem 0.7rem; font-size: 0.8rem; }
.file-btn { display: inline-flex; align-items: center; }
.link-danger { background: none; border: none; color: var(--danger-text); cursor: pointer; font-size: 0.75rem; text-decoration: underline; }

.img-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.85rem;
  max-height: min(60vh, 620px);
  overflow-y: auto;
  align-content: start;
  padding-right: 0.25rem;
}
.img-grid figure { margin: 0; cursor: pointer; position: relative; }
.img-grid figure.sel .thumb { outline: 2px solid var(--accent); }
.img-grid figure.multi-sel .thumb { outline: 2px solid var(--accent); box-shadow: 0 0 0 3px rgba(0,0,0,0.25); }
/* Drag-to-reorder affordance */
.img-grid figure.dragging { opacity: 0.4; }
.img-grid figure.drag-over .thumb { outline: 2px dashed var(--accent); outline-offset: 2px; }
.img-grid figure.drag-over::before { content: ''; position: absolute; left: -6px; top: 0; bottom: 0; width: 3px; border-radius: 2px; background: var(--accent); z-index: 6; }
.save-status { margin-left: 0.5rem; align-self: center; }

/* RAW folder matcher review panel */
.raw-match { border: 1px solid var(--border-default); background: var(--bg-elevated); border-radius: var(--radius-sm); padding: 0.75rem 1rem; margin: 0.5rem 0 1rem; }
.raw-match-head { display: flex; align-items: center; gap: 0.5rem; }
.raw-match-close { margin-left: auto; }
.raw-match-summary { margin: 0.4rem 0 0.6rem; }
.raw-match-list { list-style: none; margin: 0; padding: 0; max-height: 260px; overflow: auto; }
.raw-match-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.2rem 0; font-size: 0.9rem; }
.raw-match-row .rm-name { font-weight: 500; }
.raw-match-row .rm-arrow { color: var(--text-muted); }
.raw-match-row .rm-raw { color: var(--accent); font-family: ui-monospace, monospace; }
.raw-match-row.miss .rm-name { color: var(--text-muted); }
.rm-note { font-size: 0.78rem; margin-left: 0.25rem; }
.raw-match-unmatched { margin-top: 0.5rem; }
.raw-match-unmatched summary { cursor: pointer; }
.raw-match-actions { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }
/* Selection checkbox overlay */
.sel-check { position: absolute; top: 6px; left: 6px; z-index: 5; width: 22px; height: 22px; border-radius: 5px; background: rgba(0,0,0,0.55); border: 1px solid var(--border-default); display: flex; align-items: center; justify-content: center; cursor: pointer; opacity: 0; transition: opacity 0.12s; }
.img-grid figure:hover .sel-check, .sel-check.on { opacity: 1; }
.sel-check.on { background: var(--accent); border-color: var(--accent); }
.sel-check input { margin: 0; cursor: pointer; accent-color: var(--accent); }
/* Bulk toolbar */
.bulk-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin: 0 0 0.6rem; padding: 0.4rem 0.55rem; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 8px; }
.bulk-left, .bulk-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; }
.bulk-actions { margin-left: auto; }
.bulk-input { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.3rem 0.5rem; font-size: 0.8rem; }
.bulk-status { flex-basis: 100%; }
.thumb { aspect-ratio: 1; background: var(--bg-input); border-radius: 6px; overflow: hidden; display: flex; align-items: center; justify-content: center; position: relative; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.no-thumb { color: var(--text-muted); font-size: 0.75rem; display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }

/* Per-image variant generation status badge (top-right of each thumbnail). */
.var-badge {
  position: absolute; top: 4px; right: 4px;
  font-size: 0.62rem; font-weight: 700; line-height: 1;
  padding: 0.15rem 0.35rem; border-radius: 999px;
  background: var(--bg-overlay); color: var(--text-primary);
  display: inline-flex; align-items: center; gap: 0.2rem;
}
.var-badge.complete   { background: var(--status-converted-bg); color: var(--status-converted-text); }
.var-badge.generating { background: var(--status-contacted-bg); color: var(--status-contacted-text); }
.var-badge.none       { background: var(--bg-overlay); color: var(--text-muted); }

/* Small spinner for the "generating…" placeholder. */
.spinner {
  width: 1.1rem; height: 1.1rem; border-radius: 50%;
  border: 2px solid var(--border-default); border-top-color: var(--accent);
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.img-grid figcaption { font-size: 0.72rem; color: var(--text-muted); margin-top: 0.25rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.variants { margin-top: 0; min-width: 0; }
.variants-head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
.variants-head h4 { margin: 0; }
.variants-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.variants table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.variants th, .variants td { text-align: left; padding: 0.35rem 0.5rem; border-bottom: 1px solid var(--border-default); }

.feed-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  margin-bottom: 0.5rem;
}
.feed-head h2 { margin: 0; }
.feed-head .surface-pick { margin: 0; }
.feed-head-actions { margin-left: auto; display: flex; align-items: center; gap: 0.5rem; }
.surface-pick { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; color: var(--text-secondary); }
.surface-pick select { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.4rem 0.55rem; }

.feed-list {
  list-style: none;
  margin: 0.5rem 0 1rem;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
}
.feed-list li { display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem; border: 1px solid var(--border-default); border-radius: 6px; background: var(--bg-surface); min-width: 0; transition: opacity .12s, box-shadow .12s, border-color .12s, transform .12s; }
.feed-list li.pv-muted { grid-column: 1 / -1; }
.feed-list li.dragging { opacity: 0.4; }
.feed-list li.drag-over { border-color: var(--accent); box-shadow: 0 0 0 2px var(--accent) inset; transform: scale(1.02); }
.feed-list img { width: 46px; height: 46px; object-fit: cover; border-radius: 4px; flex-shrink: 0; }
.feed-empty { line-height: 1.45; }
.grip { cursor: grab; color: var(--text-muted); flex-shrink: 0; }
.grip:active { cursor: grabbing; }
.feed-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Picker: breathing room between the search box and the shoot list */
.picker-shoot { margin-bottom: 0.5rem; }
.picker-shoot:first-of-type { margin-top: 0.85rem; }
.picker-head { display: flex; align-items: center; gap: 0.5rem; width: 100%; text-align: left; background: var(--bg-surface-hover); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.45rem 0.6rem; cursor: pointer; transition: background .12s, border-color .12s; }
.picker-head:hover { border-color: var(--accent); }
.picker-head.open { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }
.picker-chevron { flex-shrink: 0; color: var(--text-muted); font-size: 0.75rem; }
.picker-head.open .picker-chevron { color: var(--accent); }
.picker-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The open shoot's thumbnail grid, visually tied to its (highlighted) head */
.picker-shoot.open .picker-grid { border-left: 2px solid var(--accent); margin-left: 0.3rem; padding-left: 0.6rem; }
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.4rem;
  padding: 0.5rem 0;
}
.picker-grid figure { margin: 0; cursor: pointer; aspect-ratio: 1; }
.picker-grid img { width: 100%; height: 100%; object-fit: cover; border-radius: 4px; }
.picker-grid figure .no-thumb { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background: var(--bg-input); border-radius: 4px; }

@media (max-width: 900px) { .pv-body, .home-feed { grid-template-columns: 1fr; } }

/* Bulk upload dropzone */
.dropzone {
  border: 2px dashed var(--border-default);
  border-radius: 8px;
  padding: 1.25rem;
  text-align: center;
  color: var(--text-secondary);
  background: var(--bg-surface);
  margin: 0.75rem 0;
  transition: border-color .15s, background .15s;
}
.dropzone.dragging { border-color: var(--accent); background: var(--bg-surface-hover); color: var(--text-primary); }
.upload-progress .bar { height: 8px; border-radius: 4px; background: var(--bg-surface-hover); overflow: hidden; margin-top: 0.4rem; }
.upload-progress .bar-fill { height: 100%; background: var(--accent); transition: width .2s; }
.file-btn { cursor: pointer; }

/* Clickable preview + full-screen lightbox */
.image-detail-preview.clickable { cursor: zoom-in; }
.lightbox {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, 0.9);
  display: flex; align-items: center; justify-content: center;
  padding: 2rem;
  cursor: zoom-out;
}
.lightbox-img {
  max-width: 100%; max-height: 100%;
  object-fit: contain;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
  border-radius: 4px;
}
.lightbox-close {
  position: absolute; top: 1rem; right: 1.25rem;
  background: rgba(0, 0, 0, 0.5); color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  width: 40px; height: 40px; border-radius: 50%;
  font-size: 1.1rem; cursor: pointer; line-height: 1;
}
.lightbox-close:hover { background: rgba(0, 0, 0, 0.8); }

/* Shoot credits editor */
.credits-editor { margin-top: 1rem; display: grid; gap: 1rem; }
.credit-group { border: 1px solid var(--border-subtle); border-radius: 6px; padding: 0.75rem 0.9rem; }
.credit-group-head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.5rem; }
.credit-group-head h4 { margin: 0; font-size: 0.9rem; }
.credit-hint { margin: 0 0 0.5rem; font-size: 0.78rem; }
.credit-row { display: grid; grid-template-columns: 1fr 150px auto auto; gap: 0.5rem; align-items: center; margin-bottom: 0.5rem; }
.credit-row input, .credit-row select { min-width: 0; background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.4rem 0.55rem; }
.credit-empty { margin: 0; }
.shoot-credits-summary { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.25rem; }
.credit-chip { font-size: 0.72rem; background: var(--bg-surface-hover); border: 1px solid var(--border-default); border-radius: 10px; padding: 0.1rem 0.5rem; }

/* Contributor name field with autocomplete dropdown */
.credit-person { position: relative; display: flex; align-items: center; gap: 0.35rem; }
.credit-person > input { flex: 1; }
.contrib-linked { font-size: 0.7rem; color: var(--accent); white-space: nowrap; }
.contrib-suggest { position: absolute; top: 100%; left: 0; right: 0; z-index: 60; margin: 2px 0 0; padding: 0; list-style: none; max-height: 240px; overflow-y: auto; background: var(--bg-elevated); border: 1px solid var(--border-default); border-radius: 6px; box-shadow: 0 6px 18px rgba(0,0,0,0.5); }
.contrib-suggest li { padding: 0.4rem 0.6rem; cursor: pointer; display: flex; flex-direction: column; gap: 1px; background: var(--bg-elevated); }
.contrib-suggest li:hover { background: #242424; }
.contrib-meta { font-size: 0.72rem; color: var(--text-muted); }

/* Image People chip picker */
.people-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; background: var(--bg-input); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.35rem 0.45rem; }
.people-chip { display: inline-flex; align-items: center; gap: 0.3rem; background: var(--bg-surface-active); color: var(--text-primary); border-radius: 999px; padding: 0.15rem 0.5rem; font-size: 0.8rem; }
.people-chip .chip-known { color: var(--accent); font-size: 0.7rem; }
.people-chip .chip-x { background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0; font-size: 0.75rem; line-height: 1; }
.people-chip .chip-x:hover { color: var(--text-primary); }
.people-input-wrap { position: relative; flex: 1; min-width: 120px; }
.people-input-wrap > input { width: 100%; background: transparent; color: var(--text-primary); border: none; outline: none; padding: 0.2rem 0.1rem; }
.people-add-new strong { color: var(--accent); }
/* Image Location picker */
.loc-picker { position: relative; }
.loc-picker > input { width: 100%; background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.4rem 0.55rem; }

/* Category field: dropdown + inline create */
.cat-field { display: flex; gap: 0.4rem; align-items: center; }
.cat-field select { flex: 1; min-width: 0; }
.new-category-row { display: flex; gap: 0.5rem; align-items: center; margin: 0.6rem 0; flex-wrap: wrap; }
.new-category-row input { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-default); border-radius: 6px; padding: 0.4rem 0.55rem; }

/* Contributor editor drawer (socials) */
.contrib-editor { margin-top: 0.75rem; border: 1px solid var(--accent); border-radius: 8px; padding: 0.9rem 1rem; background: var(--bg-surface); }
.contrib-editor h4 { margin: 0 0 0.6rem; font-size: 0.95rem; }
/* Social editor styles live in SocialLinksEditor.vue (scoped). */

@media (max-width: 700px) {
  .credit-row { grid-template-columns: 1fr 1fr; }
  .social-row { grid-template-columns: 1.4rem 1fr auto; }
}

/* Clickable location / credit links in the shoot header */
.link-chip { color: var(--accent); cursor: pointer; text-decoration: none; border-bottom: 1px dashed transparent; }
.link-chip:hover { border-bottom-color: var(--accent); }

/* Shoot-form location picker (picked state) */
.loc-picked { display: flex; align-items: center; gap: 0.5rem; }
.loc-picked-label { flex: 1; }

/* Read-only info modals (location / contributor) */
/* Modal chrome lives in ModalShell.vue (scoped). */
.info-avatar { width: 84px; height: 84px; border-radius: 50%; object-fit: cover; border: 1px solid var(--border-default); }
.info-socials { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.info-notes { white-space: pre-wrap; color: var(--text-muted); margin-top: 0.3rem; }
.closed-badge { margin-left: 0.5rem; font-size: 0.7rem; padding: 0.1rem 0.4rem; border-radius: 4px; background: var(--status-contacted-bg, rgba(220,80,80,0.2)); color: var(--danger-text, #ff8a8a); text-transform: uppercase; letter-spacing: 0.03em; }

/* Apply-attributes-to-shoot panel */
.apply-attrs { margin: 0.5rem 0 1rem; padding: 0.9rem 1rem; border: 1px solid var(--border-default); border-radius: 8px; background: var(--bg-overlay); }
.apply-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.3rem; }
.apply-rows { display: flex; flex-direction: column; gap: 0.55rem; margin: 0.6rem 0; }
.apply-row { display: flex; align-items: center; gap: 0.6rem; }
.apply-row .apply-label { min-width: 92px; }
.apply-row select, .apply-row input { flex: 1; min-width: 0; }
.apply-row input[type="checkbox"] { flex: 0 0 auto; }
.apply-actions { display: flex; align-items: center; gap: 0.6rem; }

/* Multi-category chip pickers (shoot + image forms) */
.cat-multi { display: flex; flex-direction: column; gap: 0.5rem; }
.cat-multi-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.cat-pill { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.25rem 0.6rem; border: 1px solid var(--border-default); border-radius: 999px; cursor: pointer; font-size: 0.85rem; background: var(--bg-overlay); user-select: none; }
.cat-pill.on { background: var(--accent); color: var(--bg-base, #0a0a0a); border-color: var(--accent); }
.cat-pill input { display: none; }

/* Duplicate-upload prompt */
.dupe-text { margin: 0 0 0.4rem; color: var(--text-primary); line-height: 1.4; }
.dupe-sub { margin: 0 0 0.9rem; }
.dupe-actions { display: flex; flex-direction: column; gap: 0.5rem; }
.dupe-actions button { width: 100%; justify-content: center; }
.dupe-all { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.9rem; font-size: 0.85rem; color: var(--text-muted); cursor: pointer; }
.dupe-all input { flex: 0 0 auto; }
</style>
